import type { JSX } from 'react'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Drawer, Box, Typography, IconButton, CircularProgress, Fab } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChatIcon from '@mui/icons-material/Chat'
import { useApiClient } from '../../api/useApiClient'
import { useAdvisor } from './useAdvisor'
import { useDatasetOverview } from './useDatasetOverview'
import { WelcomeBlock } from './WelcomeBlock'
import { MessageList } from './MessageList'
import { Composer } from './Composer'
import { pageIdForPath } from '../grounding/pageCatalog'

const DRAWER_WIDTH = 460 // R3: mid of JL's 440–480 band; judged live at the gate

/**
 * Advisor panel — a right-anchor Drawer toggled by a floating action button.
 *
 * Opens with a floating "Open advisor" Fab (bottom-right).
 * When open: shows a header, scrollable message list, a pending indicator (when
 * `pending` is true), and a message composer pinned at the bottom.
 *
 * All advisor logic lives in `useAdvisor`; this component is presentation-only.
 */
export function AdvisorPanel(): JSX.Element {
  const [open, setOpen] = useState(false)
  const apiClient = useApiClient()
  const location = useLocation()
  const overviewHandle = useDatasetOverview(apiClient, open)
  const { messages, pending, activity, send, applyNav, applyUndo, stop } =
    useAdvisor(overviewHandle)

  return (
    <>
      {!open && (
        <Fab
          color="primary"
          aria-label="Open advisor"
          onClick={() => setOpen(true)}
          size="medium"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <ChatIcon />
        </Fab>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 48,
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            Advisor
          </Typography>
          <IconButton onClick={() => setOpen(false)} aria-label="Close advisor" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Message list + pending indicator */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {messages.length === 0 ? (
            <WelcomeBlock
              onAsk={send}
              overview={overviewHandle.overview}
              pageId={pageIdForPath(location.pathname)}
            />
          ) : (
            <MessageList messages={messages} applyNav={applyNav} applyUndo={applyUndo} />
          )}
          {pending && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderTop: 1,
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {activity ?? 'Thinking…'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Composer pinned at the bottom */}
        <Composer onSend={send} onStop={stop} pending={pending} />
      </Drawer>
    </>
  )
}
